<template>
    <card-normal v-if="localContact">
        <flex-container direction="row">
            <data-group>
                <data-label>
                    E-mail
                </data-label>
                <data-value-editable>
                    <inline-edit-label>
                        {{ props.contact?.email }}
                    </inline-edit-label>

                    <template #input-group="{ setEditable }">
                        <input-group v-model="email">
                            <template #input>
                                <text-input />
                            </template>
                        </input-group>
                        <button-block
                            @click="setEditable(false); saveContactEmail();"
                        >
                            <svg-icon
                                :icon="FloppyIcon"
                                is-custom-icon
                            />
                            Opslaan
                        </button-block>
                    </template>
                </data-value-editable>
            </data-group>

            <data-group>
                <data-label>
                    Telefoon
                </data-label>
                <data-value-editable>
                    <inline-edit-label>
                        {{ props.contact?.phoneNumber }}
                    </inline-edit-label>

                    <template #input-group="{ setEditable }">
                        <input-group v-model="phoneNumber">
                            <template #input>
                                <text-input />
                            </template>
                        </input-group>
                        <button-block
                            @click="setEditable(false); saveContactPhoneNumber();"
                        >
                            <svg-icon
                                :icon="FloppyIcon"
                                is-custom-icon
                            />
                            Opslaan
                        </button-block>
                    </template>
                </data-value-editable>
            </data-group>

            <data-group>
                <data-label>
                    Adres
                </data-label>
                <data-value>
                    <inline-edit-label @click="addressModal?.openOverlay()">
                        {{ props.contact?.address.fullAddress }}
                    </inline-edit-label>
                </data-value>
            </data-group>
        </flex-container>

        <modal-dialog
            ref="addressModal"
            :size="2"
            label="Adres wijzigen"
            description="Hier kun je het adres van het contact aanpassen"
        >
            <flex-container direction="column">
                <input-group v-model="localContact.address.street">
                    <template #label>
                        <input-label>
                            Straat
                        </input-label>
                    </template>
                    <template #input>
                        <text-input placeholder="Straat" />
                    </template>
                </input-group>
                <input-group v-model="localContact.address.number">
                    <template #label>
                        <input-label>
                            Huisnummer
                        </input-label>
                    </template>
                    <template #input>
                        <text-input placeholder="Huisnummer" />
                    </template>
                </input-group>
                <input-group v-model="localContact.address.numberSuffix">
                    <template #label>
                        <input-label>
                            Huisnummer toevoeging
                        </input-label>
                    </template>
                    <template #input>
                        <text-input placeholder="Huisnummer toevoeging" />
                    </template>
                </input-group>
                <input-group v-model="localContact.address.postalCode">
                    <template #label>
                        <input-label>
                            Postcode
                        </input-label>
                    </template>
                    <template #input>
                        <text-input placeholder="Postcode" />
                    </template>
                </input-group>
                <input-group v-model="localContact.address.city">
                    <template #label>
                        <input-label>
                            Woonplaats
                        </input-label>
                    </template>
                    <template #input>
                        <text-input placeholder="Woonplaats" />
                    </template>
                </input-group>
                <input-group v-model="localContact.address.country">
                    <template #label>
                        <input-label>
                            Land
                        </input-label>
                    </template>
                    <template #input>
                        <text-input placeholder="Land" />
                    </template>
                </input-group>
                <button-block
                    @loading="saveContactAddress($event);"
                >
                    <svg-icon
                        :icon="FloppyIcon"
                        is-custom-icon
                    />
                    Opslaan
                </button-block>
            </flex-container>
        </modal-dialog>
    </card-normal>
</template>

<script setup lang="ts">
    import FloppyIcon from '@keyvue/one-awesome-icons/icons/regular/floppy-disk.svg?component';
    import type { Contact } from '@keyvue/one-api-client';
    import InlineEditLabel from '@/shared/components/input/label/InlineEditLabel.vue';
    import { onMounted, ref, watch } from 'vue';
    import { ButtonState, type ExposedOverlay, NotificationBlock, useInteractionBlocker, useNotify } from '@keyvue/one-ui';
    import { ContactAPI } from '@keyvue/one-api-client-legacy';

    const props = defineProps<{
        contact: Contact;
    }>();

    const { blockInteractionForLoad } = useInteractionBlocker();
    const { notify } = useNotify();
    const emit = defineEmits(['refresh']);

    const addressModal = ref<ExposedOverlay>();
    const localContact = ref<Contact>();
    const email = ref<string>('');
    const phoneNumber = ref<string>('');

    onMounted(() => {
        localContact.value = props.contact;
        email.value = localContact.value.email?.value ?? '';
        phoneNumber.value = localContact.value.phoneNumber?.value ?? '';
    });

    watch(props, () => {
        localContact.value = props.contact;
        email.value = localContact.value.email?.value ?? '';
        phoneNumber.value = localContact.value.phoneNumber?.value ?? '';
    }, { deep: true });

    async function saveContactAddress(event) {
        if (!localContact.value) {
            return;
        }

        addressModal.value?.closeOverlay();

        const closeInteractionBlocker = blockInteractionForLoad('Bezig met contact adres opslaan');

        const legacyContact = await ContactAPI.get(localContact.value.id.value);
        legacyContact.streetname = localContact.value.address.street;
        legacyContact.streetnumber = localContact.value.address.number;
        legacyContact.streetnumbersuffix = localContact.value.address.numberSuffix;
        legacyContact.zipcode = localContact.value.address.postalCode;
        legacyContact.city = localContact.value.address.city;
        legacyContact.country = localContact.value.address.country;
        legacyContact.country = localContact.value.address.country;

        await ContactAPI.update(
            localContact.value.id.value,
            legacyContact,
        );

        closeInteractionBlocker();

        event.button.setState(ButtonState.completed);

        notifySuccess();
        emit('refresh');
    }

    async function saveContactPhoneNumber() {
        if (!localContact.value) {
            return;
        }

        const closeInteractionBlocker = blockInteractionForLoad('Bezig met contact telefoonnummer opslaan');

        const legacyContact = await ContactAPI.get(localContact.value.id.value);

        legacyContact.phonenumber = phoneNumber.value;

        await ContactAPI.update(
            localContact.value.id.value,
            legacyContact,
        );

        closeInteractionBlocker();

        notifySuccess();
        emit('refresh');
    }

    async function saveContactEmail() {
        if (!localContact.value) {
            return;
        }

        const closeInteractionBlocker = blockInteractionForLoad('Bezig met contact e-mailadres opslaan');

        const legacyContact = await ContactAPI.get(localContact.value.id.value);

        legacyContact.email = email.value;

        await ContactAPI.update(
            localContact.value.id.value,
            legacyContact,
        );

        closeInteractionBlocker();

        notifySuccess();
        emit('refresh');
    }

    function notifySuccess() {
        notify({
            component: NotificationBlock,
            props: {
                title: 'Gelukt!',
                text: 'We hebben het contact opgeslagen.',
                color: 'green-500',
            },
            duration: 4000,
        });
    }
</script>
